export const isSafari = () => {
	return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
};

export const isIOSDevice = () => {
	return /iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
};

export const isFirefox = typeof navigator !== 'undefined' && /firefox/i.test(navigator.userAgent);

// Operating system types
export type OperatingSystem = 'windows' | 'macos' | 'chromeos' | 'linux' | 'ios' | 'android' | 'unknown';

// Browser types
export type BrowserName = 'chrome' | 'edge' | 'safari' | 'firefox' | 'unknown';

/**
 * Detect the operating system from the user agent string.
 * Order matters: more specific checks come before general ones.
 */
export const getOperatingSystem = (): OperatingSystem => {
	if (typeof navigator === 'undefined') return 'unknown';

	const ua = navigator.userAgent;

	// Check iOS first (before macOS, since iOS Safari reports Mac in UA)
	if (/iPhone|iPad|iPod/.test(ua)) return 'ios';

	// Check Android
	if (/Android/.test(ua)) return 'android';

	// Check ChromeOS (before Windows/Mac checks since it may contain those strings)
	if (/CrOS/.test(ua)) return 'chromeos';

	// Check Windows
	if (/Windows/.test(ua)) return 'windows';

	// Check macOS (after iOS check)
	if (/Macintosh|Mac OS X/.test(ua)) return 'macos';

	// Check Linux (after Android and ChromeOS)
	if (/Linux/.test(ua)) return 'linux';

	return 'unknown';
};

/**
 * Detect the browser name from the user agent string.
 * Order matters: Edge contains "Chrome" in UA, so check Edge first.
 */
export const getBrowserName = (): BrowserName => {
	if (typeof navigator === 'undefined') return 'unknown';

	const ua = navigator.userAgent;

	// Edge (Chromium-based) - check before Chrome since Edge UA contains "Chrome"
	if (/Edg\//.test(ua)) return 'edge';

	// Firefox
	if (/Firefox/.test(ua)) return 'firefox';

	// Safari - use existing isSafari logic (excludes Chrome on iOS)
	if (isSafari()) return 'safari';

	// Chrome (after Edge check)
	if (/Chrome/.test(ua)) return 'chrome';

	return 'unknown';
};

/**
 * Dual-capture support result type.
 * - system: Can capture all system audio (Windows, ChromeOS)
 * - tab: Can only capture tab/browser audio (macOS, Linux)
 */
export type DualCaptureSupport = { supported: true; audioType: 'system' | 'tab' } | { supported: false; reason: 'unsupported-browser' | 'unsupported-os' };

/**
 * Check if the current browser/OS combination supports dual-capture recording.
 *
 * Dual-capture uses getDisplayMedia to capture system/tab audio in addition to
 * microphone audio. This enables capturing both sides of a conversation.
 *
 * Support matrix:
 * - Chrome/Edge on Windows/ChromeOS: Full system audio capture
 * - Chrome/Edge on macOS/Linux: Tab audio only (browser meetings work)
 * - Safari/Firefox: No audio capture via getDisplayMedia
 * - Mobile: Not supported (no getDisplayMedia)
 */
export const getDualCaptureSupport = (): DualCaptureSupport => {
	const browser = getBrowserName();
	const os = getOperatingSystem();

	// Safari and Firefox don't support audio in getDisplayMedia
	if (browser === 'safari' || browser === 'firefox') {
		return { supported: false, reason: 'unsupported-browser' };
	}

	// Mobile platforms don't support getDisplayMedia
	if (os === 'ios' || os === 'android') {
		return { supported: false, reason: 'unsupported-os' };
	}

	// Chrome/Edge on compatible platforms
	if (browser === 'chrome' || browser === 'edge') {
		// Windows and ChromeOS support full system audio capture
		if (os === 'windows' || os === 'chromeos') {
			return { supported: true, audioType: 'system' };
		}

		// macOS and Linux support tab audio only
		if (os === 'macos' || os === 'linux') {
			return { supported: true, audioType: 'tab' };
		}
	}

	// Unknown browser/OS combination
	return { supported: false, reason: 'unsupported-os' };
};
